/**
 * Trainer avatar → image URL resolution.
 *
 * A trainer avatar is stored as CROP METADATA (`TrainerAvatarCrop`), not as a URL: either a
 * campaign portrait id (the player's own Keeper, or a cast member) or a species + stage, plus
 * the crop/zoom the player framed it with. {@link TrainerAvatar} renders the crop; surfaces
 * that can only take a plain `src` (the match-search window's fighter plate, say) resolve the
 * image here, so the mapping lives in ONE place rather than being re-derived per screen.
 */

import type { TrainerAvatarCrop } from '@shared/api/contracts';
import { getPetStageImageUrl } from './petHelpers';
import { campaignPortraitSrcById, PLAYER_PORTRAIT_ID, PLAYER_PORTRAIT_SRC } from './campaignPortraits';

/** Where the default Keeper portrait's face sits, so its circle crop shows the face, not the shirt. */
const KEEPER_FACE_CROP_Y = 0.4;
const KEEPER_FACE_ZOOM = 1.15;

/**
 * The avatar a player wears until they pick one: the default Keeper art, framed on the face.
 * A placeholder glyph read as missing data (or as a third speaker) in the story dialogue, and
 * the game already has art for exactly this person — the player.
 */
export const DEFAULT_KEEPER_AVATAR: TrainerAvatarCrop = {
  portraitId: PLAYER_PORTRAIT_ID,
  cropX: 0.5,
  cropY: KEEPER_FACE_CROP_Y,
  zoom: KEEPER_FACE_ZOOM,
};

/** Image src for an avatar: campaign portrait first, else the species image. Null if neither. */
export function resolveTrainerAvatarUrl(avatar: TrainerAvatarCrop | null | undefined): string | null {
  if (!avatar) return null;
  if (avatar.portraitId) return campaignPortraitSrcById(avatar.portraitId);
  if (avatar.speciesId != null && avatar.stage != null) {
    return getPetStageImageUrl(avatar.speciesId, avatar.stage);
  }
  return null;
}

/**
 * The player's own portrait for a campaign screen: their chosen avatar when they have one,
 * otherwise the default Keeper art. Never null — a campaign screen always draws a trainer
 * on the player's side, because the rival side is always a trainer too.
 */
export function playerCampaignPortraitSrc(avatar: TrainerAvatarCrop | null | undefined): string {
  return resolveTrainerAvatarUrl(avatar) ?? PLAYER_PORTRAIT_SRC;
}
